import { Injectable, computed, inject, signal } from '@angular/core';

import { Observable, of, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Apps } from '@core/models/app-switcher.enum';
import { CompanyApp } from '@core/models/company-api.models';
import { SalesChannel } from '@core/models/intake.enum';

import { CompanyApiService } from './company-api.service';

/** Every channel an item could be offered on, POS first. */
const ALL_CHANNELS: readonly SalesChannel[] = [SalesChannel.Pos, SalesChannel.Lister];

/** Company app → the sales channel it unlocks. Apps with no channel are ignored. */
const CHANNEL_BY_APP: Partial<Record<Apps, SalesChannel>> = {
  [Apps.POS]: SalesChannel.Pos,
  [Apps.Lister]: SalesChannel.Lister
};

/**
 * Resolves which sales channels the company may list items on, from the Company
 * API's `Companies/{id}/Apps`: the POS app unlocks the POS channel and the Lister
 * app unlocks Lister. A company with neither cannot take items in at all — see
 * `isBlocked`, which the app shell turns into a sign-out prompt.
 *
 * Until the call resolves (and if it fails) `channels` reports every channel, so a
 * Company API outage never silently narrows what staff can select.
 */
@Injectable({ providedIn: 'root' })
export class SalesChannelAccessService {
  private readonly companyApi = inject(CompanyApiService);

  /** null until the Company API answers. */
  private readonly _channels = signal<SalesChannel[] | null>(null);
  private loadedCompanyId: number | null = null;

  /** Channels the company can sell on; every channel while unresolved. */
  readonly channels = computed<SalesChannel[]>(() => this._channels() ?? [...ALL_CHANNELS]);

  /** True only once the API has confirmed the company has neither POS nor Lister. */
  readonly isBlocked = computed(() => this._channels()?.length === 0);

  /** Fetches once per company; repeat calls replay the resolved list. */
  load(companyId: number): Observable<SalesChannel[]> {
    if (this.loadedCompanyId === companyId && this._channels() !== null) {
      return of(this._channels()!);
    }
    this.loadedCompanyId = companyId;

    return this.companyApi.getCompanyApps(companyId).pipe(
      map(res => toChannels(res.apps ?? [])),
      tap(channels => this._channels.set(channels)),
      catchError(() => {
        // Fail open: an unreachable Company API must not lock staff out of intake.
        this.loadedCompanyId = null;
        return of([...ALL_CHANNELS]);
      })
    );
  }
}

function toChannels(apps: CompanyApp[]): SalesChannel[] {
  const granted = new Set(apps.map(a => CHANNEL_BY_APP[a.id as Apps]).filter((c): c is SalesChannel => c != null));
  return ALL_CHANNELS.filter(c => granted.has(c));
}
